import React, { useState, useEffect, useRef } from 'react';
import { Download, ZoomIn, ZoomOut, RotateCcw, Layers, ShoppingCart, BarChart2, BookOpen, Search, TrendingUp, Globe, Cpu, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';

interface FlowchartTab {
    id: string;
    label: string;
    shortLabel?: string;
    icon: React.ReactNode;
    svgFile: string;
    pdfFile: string;
    description: string;
    badge?: string;
    color: string;
}

const TABS: FlowchartTab[] = [
    {
        id: 'arquitectura',
        label: 'Arquitectura General',
        shortLabel: 'Arquitectura',
        icon: <Layers size={15} />,
        svgFile: '/FLUJOGRAMA_ARQUITECTURA_GENERAL_PETRAL_V1.svg',
        pdfFile: '/FLUJOGRAMA_ARQUITECTURA_GENERAL_PETRAL_V1.pdf',
        description: '5 niveles: Maestros → Spot Engine → Motores P×Q → Ledger → Forecast → Auditoría',
        badge: 'Nivel 1→5',
        color: 'blue',
    },
    {
        id: 'multicotizador',
        label: 'Multicotizador',
        shortLabel: 'Multicotizador',
        icon: <ShoppingCart size={15} />,
        svgFile: '/FLOWCHART_MULTICOTIZADOR.svg',
        pdfFile: '/FLOWCHART_MULTICOTIZADOR.pdf',
        description: 'Inputs → Validación Maestros → Spot Engine → Costos Portuarios → P&L → Exportar a Matriz',
        badge: '7 Pasos',
        color: 'amber',
    },
    {
        id: 'motor-baf',
        label: 'Motor BAF',
        shortLabel: 'Motor BAF',
        icon: <Zap size={15} />,
        svgFile: '/FLOWCHART_MOTOR_BAF.svg',
        pdfFile: '/FLOWCHART_MOTOR_BAF.svg',
        description: 'Inputs BAF → Estructura Polinómica (38.40 IFO / 9.50 MDO) → Factor fa → Delta BAF USD/PMT → Tiers',
        badge: 'Ajuste Bunker',
        color: 'emerald',
    },
    {
        id: 'motor-pxq',
        label: 'Motor P×Q',
        shortLabel: 'Motor P×Q',
        icon: <Cpu size={15} />,
        svgFile: '/FLOWCHART_MOTOR_PXQ.svg',
        pdfFile: '/FLOWCHART_MOTOR_PXQ.pdf',
        description: 'Inputs Buque & Operación → Core Dispatcher → Motor Calculador Σ(P×Q) → Tabla Desglose Granular',
        badge: 'Tarificador P×Q',
        color: 'amber',
    },
    {
        id: 'voyage-ledger',
        label: 'Voyage Ledger',
        shortLabel: 'Voyage Ledger',
        icon: <BookOpen size={15} />,
        svgFile: '/FLOWCHART_VOYAGE_LEDGER.svg',
        pdfFile: '/FLOWCHART_VOYAGE_LEDGER.pdf',
        description: 'Configuración del viaje → Cálculo automático P&L → Desglose por ítem → Sensibilidad → Exportar',
        badge: 'P&L Viaje',
        color: 'teal',
    },
    {
        id: 'matriz-financiera',
        label: 'Matriz Financiera',
        shortLabel: 'Matriz Financiera',
        icon: <BarChart2 size={15} />,
        svgFile: '/FLOWCHART_MATRIZ_FINANCIERA.svg',
        pdfFile: '/FLOWCHART_MATRIZ_FINANCIERA.pdf',
        description: 'Viajes exportados → Grilla Mensual Multi-Cliente → Vistas Gráfico / Ledger → Excel / Auditoría',
        badge: 'Forecast',
        color: 'orange',
    },
    {
        id: 'analisis-grafico',
        label: 'Análisis Gráfico',
        shortLabel: 'Análisis Gráfico',
        icon: <TrendingUp size={15} />,
        svgFile: '/FLOWCHART_ANALISIS_GRAFICO.svg',
        pdfFile: '/FLOWCHART_ANALISIS_GRAFICO.pdf',
        description: 'P&L vs Yield & Demurrage → SpaghettiMap V2 con misiles → Filtros de clientes y operaciones',
        badge: 'Analítica',
        color: 'blue',
    },
    {
        id: 'spaghetti-map',
        label: 'Spaghetti Map V2',
        shortLabel: 'Spaghetti Map',
        icon: <Globe size={15} />,
        svgFile: '/FLOWCHART_SPAGHETTI_MAP.svg',
        pdfFile: '/FLOWCHART_SPAGHETTI_MAP.pdf',
        description: 'Mapa base GeoJSON → Pasteles duales Fuentes/Sumideros → Curvas Bezier con misiles animado',
        badge: 'GeoJSON',
        color: 'teal',
    },
    {
        id: 'auditoria-dual',
        label: 'Liquidador de Gastos Portuarios',
        shortLabel: 'Liquidador P×Q',
        icon: <Search size={15} />,
        svgFile: '/FLOWCHART_AUDITORIA_DUAL.svg',
        pdfFile: '/FLOWCHART_AUDITORIA_DUAL.pdf',
        description: 'PDFs Armador & Agente → Split-View → Comparación Δ Bunkers & Puertos (PxQ) → Acta PDF exportable',
        badge: 'Liquidador P×Q',
        color: 'slate',
    },
];



const COLOR_MAP: Record<string, { active: string; badge: string; icon: string }> = {
    blue:    { active: 'bg-white text-blue-700 border-blue-200 shadow-sm',     badge: 'bg-blue-50 text-blue-600 border-blue-200',     icon: 'bg-blue-50 border-blue-200 text-blue-600' },
    amber:   { active: 'bg-white text-amber-700 border-amber-200 shadow-sm',   badge: 'bg-amber-50 text-amber-600 border-amber-200',   icon: 'bg-amber-50 border-amber-200 text-amber-600' },
    emerald: { active: 'bg-white text-emerald-700 border-emerald-200 shadow-sm', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: 'bg-emerald-50 border-emerald-200 text-emerald-600' },
    teal:    { active: 'bg-white text-teal-700 border-teal-200 shadow-sm',     badge: 'bg-teal-50 text-teal-600 border-teal-200',     icon: 'bg-teal-50 border-teal-200 text-teal-600' },
    orange:  { active: 'bg-white text-orange-700 border-orange-200 shadow-sm', badge: 'bg-orange-50 text-orange-600 border-orange-200', icon: 'bg-orange-50 border-orange-200 text-orange-600' },
    purple:  { active: 'bg-white text-purple-700 border-purple-200 shadow-sm', badge: 'bg-purple-50 text-purple-600 border-purple-200', icon: 'bg-purple-50 border-purple-200 text-purple-600' },
    indigo:  { active: 'bg-white text-indigo-700 border-indigo-200 shadow-sm', badge: 'bg-indigo-50 text-indigo-600 border-indigo-200', icon: 'bg-indigo-50 border-indigo-200 text-indigo-600' },
    rose:    { active: 'bg-white text-rose-700 border-rose-200 shadow-sm',     badge: 'bg-rose-50 text-rose-600 border-rose-200',     icon: 'bg-rose-50 border-rose-200 text-rose-600' },
    slate:   { active: 'bg-white text-slate-700 border-slate-300 shadow-sm',   badge: 'bg-slate-100 text-slate-600 border-slate-200',   icon: 'bg-slate-100 border-slate-200 text-slate-600' },
};

export const SystemFlowchartViewer_V2: React.FC = () => {
    const [zoomLevel, setZoomLevel] = useState<number>(100);
    const [activeTab, setActiveTab] = useState<string>('arquitectura');
    const containerRef = useRef<HTMLDivElement>(null);

    // Zoom interactivo con Ctrl + Scroll del mouse (intercepta preventDefault sin pasividad)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
                if (e.deltaY < 0) {
                    setZoomLevel(prev => Math.min(prev + 10, 250));
                } else {
                    setZoomLevel(prev => Math.max(prev - 10, 40));
                }
            }
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', handleWheel);
        };
    }, []);

    const currentTab = TABS.find(t => t.id === activeTab) ?? TABS[0];
    const colors = COLOR_MAP[currentTab.color] || COLOR_MAP.blue;

    const handleZoomIn    = () => setZoomLevel(prev => Math.min(prev + 20, 250));
    const handleZoomOut   = () => setZoomLevel(prev => Math.max(prev - 20, 40));
    const handleResetZoom = () => setZoomLevel(100);

    const handleDownloadPdf = () => {
        const link = document.createElement('a');
        link.href = currentTab.pdfFile;
        link.download = currentTab.pdfFile.replace('/', '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <section className="flex flex-col gap-4 relative mt-2 animate-in fade-in slide-in-from-bottom-2 duration-300 flex-1 h-full min-h-screen">

            {/* ── TAB BAR ── */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {/* Tabs en 1 sola fila continua (sin scroll horizontal, sin iconos) */}
                <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-1.5 p-1.5 bg-slate-100/90 border-b border-slate-200 w-full">
                    {TABS.map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => { setActiveTab(tab.id); setZoomLevel(100); }}
                                className={`w-full py-2 px-1 text-center font-bold text-[11px] xl:text-xs rounded-lg transition-all border flex items-center justify-center cursor-pointer select-none truncate ${
                                    isActive 
                                        ? 'bg-white text-blue-700 border-blue-200 shadow-xs font-black' 
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 border-transparent'
                                }`}
                                title={tab.label}
                            >
                                <span className="truncate">{tab.shortLabel || tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Header Ribbon */}
                <div className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2.5 border rounded-lg ${colors.icon}`}>
                            {currentTab.icon}
                        </div>
                        <div>
                            <h2 className="text-sm font-black tracking-tight text-slate-800 flex items-center gap-2 uppercase">
                                {currentTab.label}
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors.badge}`}>
                                    {currentTab.badge}
                                </span>
                            </h2>
                            <p className="text-xs text-slate-500 font-medium mt-0.5 max-w-xl">{currentTab.description}</p>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
                            <Button variant="ghost" size="sm" onClick={handleZoomOut}
                                className="h-8 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200 cursor-pointer" title="Reducir Zoom">
                                <ZoomOut size={14} />
                            </Button>
                            <span className="text-xs font-mono font-bold text-slate-700 px-2 min-w-[50px] text-center select-none">
                                {zoomLevel}%
                            </span>
                            <Button variant="ghost" size="sm" onClick={handleZoomIn}
                                className="h-8 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200 cursor-pointer" title="Aumentar Zoom">
                                <ZoomIn size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={handleResetZoom}
                                className="h-8 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200 cursor-pointer" title="Resetear Zoom">
                                <RotateCcw size={14} />
                            </Button>
                        </div>
                        <Button onClick={handleDownloadPdf}
                            className="h-9 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-2 px-4 shadow-sm transition-all cursor-pointer">
                            <Download size={14} />
                            <span>Descargar PDF</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* ── FLOWCHART VIEWER (SOPORTA CTRL + SCROLL ZOOM) ── */}
            <div 
                ref={containerRef}
                title="Tip: Usa Ctrl + Scroll del mouse para acercar o alejar el diagrama"
                className="bg-slate-100 border border-slate-200 rounded-xl shadow-inner p-6 overflow-x-auto overflow-y-auto max-h-[85vh] flex flex-col items-center justify-start select-none"
            >
                <div
                    className="transition-all duration-200 ease-out flex flex-col items-center min-w-[1600px]"
                    style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }}
                >
                    <img
                        key={currentTab.svgFile}
                        src={currentTab.svgFile}
                        alt={`Flowchart: ${currentTab.label}`}
                        className="w-[1600px] h-auto shadow-lg rounded-xl bg-white p-8 border border-slate-200"
                    />
                </div>
            </div>
        </section>
    );
};
